/** Format ISO date (2026-02-23) to Dutch (23-02-2026) */
export function fmtDatum(d) {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

/** Parse "HH:MM" to minutes since midnight */
export function tijdNaarMinuten(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const PAUZE_START = 12 * 60 + 30; // 12:30
const PAUZE_EIND = 13 * 60;       // 13:00

/** Bereken netto uren met automatische pauze-aftrek (12:30-13:00) */
export function berekenUrenMetPauze(startTijd, eindTijd) {
  if (!startTijd || !eindTijd) return { bruto: 0, netto: 0, pauzeTrekt: false };
  const start = tijdNaarMinuten(startTijd);
  const eind = tijdNaarMinuten(eindTijd);
  if (eind <= start) return { bruto: 0, netto: 0, pauzeTrekt: false };

  const brutoMin = eind - start;
  const pauzeTrekt = start < PAUZE_START && eind > PAUZE_EIND;
  const overlapStart = Math.max(start, PAUZE_START);
  const overlapEind = Math.min(eind, PAUZE_EIND);
  const pauzeOverlap = pauzeTrekt ? Math.max(0, overlapEind - overlapStart) : 0;
  const nettoMin = brutoMin - pauzeOverlap;

  return {
    bruto: brutoMin / 60,
    netto: nettoMin / 60,
    pauzeTrekt,
    pauzeMinuten: pauzeOverlap,
  };
}

const WERKDAG_START = '08:00';
const WERKDAG_EIND = '17:00';
const UREN_PER_WERKDAG = 8; // netto (8:00-17:00 minus 30min pauze = 8.5h - 0.5h = 8h)

/** Map JS Date (or day index 0=zo..6=za) to werkrooster key */
const DAGKEY_MAP = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
export function dagKeyVanDatum(d) {
  const dt = typeof d === 'string' ? new Date(d + 'T12:00:00') : d;
  return DAGKEY_MAP[dt.getDay()];
}

/** Parse werkrooster blokken voor een dag, returns [{start, eind}] in "HH:MM" format */
function parseRoosterBlokken(rooster, dagKey) {
  if (!rooster || !rooster[dagKey]) return null; // null = geen rooster → gebruik default
  const dag = rooster[dagKey];
  const blokken = [];
  ['ochtend', 'middag', 'avond'].forEach(deel => {
    const val = dag[deel];
    if (!val) return;
    const parts = val.split('-');
    if (parts.length !== 2) return;
    const s = parts[0].trim();
    const e = parts[1].trim();
    if (s && e) blokken.push({ van: s, tot: e });
  });
  return blokken; // empty array = dag zonder werk
}

/** Bereken netto werkuren voor een dag obv werkrooster (met pauze-aftrek) */
export function getWerkUrenDag(werkrooster, datum) {
  const dk = dagKeyVanDatum(datum);
  const isWeekend = dk === 'za' || dk === 'zo';

  if (werkrooster) {
    const blokken = parseRoosterBlokken(werkrooster, dk);
    if (blokken === null) {
      // Dag niet in rooster gedefinieerd → default: doordeweeks 8u, weekend 0
      return isWeekend ? { werkt: false, uren: 0, startTijd: null, eindTijd: null } :
        { werkt: true, uren: UREN_PER_WERKDAG, startTijd: WERKDAG_START, eindTijd: WERKDAG_EIND };
    }
    if (blokken.length === 0) return { werkt: false, uren: 0, startTijd: null, eindTijd: null };
    // Tel uren over alle blokken met pauze-aftrek
    let totaal = 0;
    let vroegsteStart = null, laatsteEind = null;
    blokken.forEach(b => {
      const info = berekenUrenMetPauze(b.van, b.tot);
      totaal += info.netto;
      if (!vroegsteStart || b.van < vroegsteStart) vroegsteStart = b.van;
      if (!laatsteEind || b.tot > laatsteEind) laatsteEind = b.tot;
    });
    return { werkt: totaal > 0, uren: Math.round(totaal * 100) / 100, startTijd: vroegsteStart, eindTijd: laatsteEind };
  }

  // Geen werkrooster: standaard ma-vr 8u
  if (isWeekend) return { werkt: false, uren: 0, startTijd: null, eindTijd: null };
  return { werkt: true, uren: UREN_PER_WERKDAG, startTijd: WERKDAG_START, eindTijd: WERKDAG_EIND };
}

/**
 * Bereken uren voor een periode met specifieke begin- en eindtijd.
 * Houdt rekening met werkrooster als meegegeven.
 *
 * @param {string} startDatum - ISO datum (yyyy-mm-dd)
 * @param {string} startTijd  - HH:MM
 * @param {string} eindDatum  - ISO datum (yyyy-mm-dd)
 * @param {string} eindTijd   - HH:MM
 * @param {string[]} feestdagen - Array van ISO datums die geen werkdag zijn
 * @param {object} [werkrooster] - Werkrooster object van medewerker
 * @returns {{ netto: number, details: Array, werkdagen: number }}
 */
export function berekenUrenPeriode(startDatum, startTijd, eindDatum, eindTijd, feestdagen = [], werkrooster = null) {
  try {
  if (!startDatum || !eindDatum) return { netto: 0, details: [], werkdagen: 0 };

  const feestSet = new Set(feestdagen);
  const isWerkdag = (d, iso) => {
    if (feestSet.has(iso)) return false;
    const dagInfo = getWerkUrenDag(werkrooster, d);
    return dagInfo.werkt;
  };

  const start = new Date(startDatum + 'T12:00:00');
  const eind = new Date(eindDatum + 'T12:00:00');
  if (eind < start) return { netto: 0, details: [], werkdagen: 0 };

  const details = [];
  let totaalNetto = 0;
  let werkdagenTel = 0;

  // Zelfde dag
  if (startDatum === eindDatum) {
    if (!isWerkdag(start, startDatum)) return { netto: 0, details: [{ datum: startDatum, type: 'niet-werkdag', uren: 0 }], werkdagen: 0 };
    const dagInfo = getWerkUrenDag(werkrooster, start);

    if (!startTijd && !eindTijd) {
      // Geen tijden → hele werkdag
      details.push({ datum: startDatum, type: 'hele dag', uren: dagInfo.uren });
      return { netto: dagInfo.uren, details, werkdagen: 1 };
    }

    const effectiefStart = startTijd || dagInfo.startTijd || WERKDAG_START;
    const effectiefEind = eindTijd || dagInfo.eindTijd || WERKDAG_EIND;
    const info = berekenUrenMetPauze(effectiefStart, effectiefEind);
    details.push({ datum: startDatum, type: 'dagdeel', van: effectiefStart, tot: effectiefEind, uren: info.netto, pauzeTrekt: info.pauzeTrekt });
    return { netto: Math.round(info.netto * 100) / 100, details, werkdagen: 1 };
  }

  // Meerdere dagen
  const current = new Date(start);
  while (current <= eind) {
    const iso = current.toISOString().split('T')[0];

    if (feestSet.has(iso)) {
      details.push({ datum: iso, type: 'feestdag', uren: 0 });
    } else {
      const dagInfo = getWerkUrenDag(werkrooster, current);
      if (!dagInfo.werkt) {
        details.push({ datum: iso, type: 'niet-werkdag', uren: 0 });
      } else if (iso === startDatum) {
        if (startTijd) {
          // Eerste dag: startTijd → einde werkdag
          const dagEind = dagInfo.eindTijd || WERKDAG_EIND;
          const info = berekenUrenMetPauze(startTijd, dagEind);
          details.push({ datum: iso, type: 'eerste dag', van: startTijd, tot: dagEind, uren: info.netto, pauzeTrekt: info.pauzeTrekt });
          totaalNetto += info.netto;
        } else {
          // Geen startTijd: hele werkdag
          details.push({ datum: iso, type: 'hele dag', uren: dagInfo.uren });
          totaalNetto += dagInfo.uren;
        }
        werkdagenTel++;
      } else if (iso === eindDatum) {
        if (eindTijd) {
          // Laatste dag: begin werkdag (volgens rooster) → eindTijd
          const dagStart = dagInfo.startTijd || WERKDAG_START;
          const info = berekenUrenMetPauze(dagStart, eindTijd);
          details.push({ datum: iso, type: 'laatste dag', van: dagStart, tot: eindTijd, uren: info.netto, pauzeTrekt: info.pauzeTrekt });
          totaalNetto += info.netto;
        } else {
          // Geen eindTijd: hele werkdag volgens rooster
          details.push({ datum: iso, type: 'hele dag', uren: dagInfo.uren });
          totaalNetto += dagInfo.uren;
        }
        werkdagenTel++;
      } else {
        // Tussenliggende werkdag: werkrooster-uren
        details.push({ datum: iso, type: 'hele dag', uren: dagInfo.uren });
        totaalNetto += dagInfo.uren;
        werkdagenTel++;
      }
    }

    current.setDate(current.getDate() + 1);
  }

  return { netto: Math.round(totaalNetto * 100) / 100, details, werkdagen: werkdagenTel };
  } catch (err) { console.error('berekenUrenPeriode fout:', err); return { netto: 0, details: [], werkdagen: 0 }; }
}

/**
 * Bereken verlofuren voor hele-dag-modus (zonder specifieke begin/eindtijd).
 * Respecteert werkrooster per dag ipv standaard 8u.
 */
export function berekenVerlofUrenHeleDag(startDatum, eindDatum, feestdagen = [], werkrooster = null) {
  try {
  if (!startDatum || !eindDatum) return { uren: 0, werkdagen: 0 };
  const feestSet = new Set(feestdagen);
  const start = new Date(startDatum + 'T12:00:00');
  const eind = new Date(eindDatum + 'T12:00:00');
  if (eind < start) return { uren: 0, werkdagen: 0 };

  let totaal = 0, werkdagen = 0;
  const current = new Date(start);
  while (current <= eind) {
    const iso = current.toISOString().split('T')[0];
    if (!feestSet.has(iso)) {
      const dagInfo = getWerkUrenDag(werkrooster, current);
      if (dagInfo.werkt) {
        totaal += dagInfo.uren;
        werkdagen++;
      }
    }
    current.setDate(current.getDate() + 1);
  }
  return { uren: Math.round(totaal * 100) / 100, werkdagen };
  } catch (err) { console.error('berekenVerlofUrenHeleDag fout:', err); return { uren: 0, werkdagen: 0 }; }
}
